import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { getMessagesFromRequest } from 'lib/api/chat';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { buildRequestId } from 'pages/Finance/Components/Financing/common/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export interface ChatMessage {
  id: string;
  userId: number;
  text: string;
  userName: string;
  userRole?: string;
  timestamp: Date;
}

interface ChatMessagesDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRequest: any;
  requestType: string;
  selectedMessages: string[];
  onSelectionChange: (selectedMessages: ChatMessage[]) => void;
}

const colorsForUsernamesInHex = [
  '#2196F3',
  '#43A047',
  '#FB8C00',
  '#E53935',
  '#8E24AA',
  '#546E7A',
  '#009688',
  '#FBC02D',
  '#D81B60',
  '#6D4C41',
];

// Componente de mensaje con checkbox (inspirado en ChatMessage)
interface SelectableChatMessageProps {
  message: ChatMessage;
  checked: boolean;
  onToggle: () => void;
  currentUserId?: number;
}

const SelectableChatMessage: React.FC<SelectableChatMessageProps> = ({
  message,
  checked,
  onToggle,
  currentUserId,
}) => {
  const isMine = message.userId === currentUserId;
  const userColor = colorsForUsernamesInHex[message.userId % 10];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.5,
        px: 1,
      }}
    >
      <Checkbox
        checked={checked}
        onChange={onToggle}
        color="primary"
        sx={{ mt: 0.5 }}
      />
      <Paper
        elevation={2}
        sx={{
          flex: 1,
          p: 1.5,
          pt: 1,
          pb: 1,
          backgroundColor: isMine ? '#EDF0FD' : '#FFFFFF',
          borderRadius: 2,
          maxWidth: '100%',
        }}
      >
        <Typography
          style={{
            fontSize: '0.90rem',
            color: userColor,
            fontWeight: 'bold',
          }}
          variant="body1"
        >
          {message.userName}
          {message.userRole && ` | ${message.userRole}`}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'space-between',
            color: 'black',
            mt: 0.5,
          }}
        >
          <Typography
            variant="body1"
            style={{
              color: 'black',
              fontWeight: 'normal',
            }}
          >
            {message.text}
          </Typography>
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              ml: 1,
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export const ChatMessagesDialog: React.FC<ChatMessagesDialogProps> = ({
  open,
  onClose,
  selectedRequest,
  requestType,
  selectedMessages,
  onSelectionChange,
}) => {
  const dispatch = useDispatch();
  const [localSelection, setLocalSelection] =
    useState<string[]>(selectedMessages);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Cargar mensajes del chat cuando se abre el dialog
  useEffect(() => {
    if (open && selectedRequest) {
      const fetchMessages = async () => {
        setLoading(true);
        try {
          const requestId = buildRequestId(selectedRequest, requestType);
          const response = await getMessagesFromRequest(dispatch, {
            requestType,
            requestId: Number(requestId),
          });

          if (response?.messages) {
            // Mapear mensajes de la API al formato esperado
            const mappedMessages: ChatMessage[] = response.messages.map(
              (msg: any, index: number) => ({
                id: msg.id || `msg-${index}-${msg.timestamp}`,
                userId: msg.userId || 0,
                text: msg.text || '',
                userName: msg.userName || 'Usuario',
                userRole: msg.userRole || '',
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              }),
            );
            setMessages(mappedMessages);

            // Si no hay mensajes seleccionados, seleccionar todos por defecto
            if (selectedMessages.length === 0 && mappedMessages.length > 0) {
              const allIds = mappedMessages.map((msg) => msg.id);
              setLocalSelection(allIds);
            }
          }
        } catch (error) {
          console.error('Error fetching messages:', error);
          setMessages([]);
        } finally {
          setLoading(false);
        }
      };

      void fetchMessages();
    }
  }, [open, selectedRequest, requestType, dispatch]);

  // Sincronizar localSelection con selectedMessages cuando cambian
  useEffect(() => {
    setLocalSelection(selectedMessages);
  }, [selectedMessages]);

  const handleToggleMessage = (messageId: string) => {
    setLocalSelection((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId],
    );
  };

  const handleSelectAll = () => {
    const allIds = messages.map((msg) => msg.id);
    setLocalSelection(allIds);
  };

  const handleClear = () => {
    setLocalSelection([]);
  };

  const handleAccept = () => {
    // Mapear IDs seleccionados a objetos completos de mensajes
    const selectedMessageObjects = messages.filter((msg) =>
      localSelection.includes(msg.id),
    );
    onSelectionChange(selectedMessageObjects);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelection(selectedMessages);
    onClose();
  };

  const selectedCount = localSelection.length;
  const totalCount = messages.length;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Selecciona los mensajes a exportar como observaciones
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CenteredCircularProgress type="dialog" />
          </Box>
        ) : (
          <Grid container sx={{ height: '60vh', minHeight: 500 }}>
            {/* Panel izquierdo: Mensajes */}
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {/* Header bar */}
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  {selectedCount} de {totalCount} seleccionados
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CustomButton
                    size="small"
                    variant="text"
                    onClick={handleSelectAll}
                    disabled={messages.length === 0}
                  >
                    Seleccionar todos
                  </CustomButton>
                  <CustomButton
                    size="small"
                    variant="text"
                    onClick={handleClear}
                    disabled={messages.length === 0}
                  >
                    Limpiar
                  </CustomButton>
                </Box>
              </Box>

              {/* Lista de mensajes scrollable */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                {messages.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', mt: 4 }}
                  >
                    No hay mensajes disponibles
                  </Typography>
                ) : (
                  messages.map((message) => (
                    <SelectableChatMessage
                      key={message.id}
                      message={message}
                      checked={localSelection.includes(message.id)}
                      onToggle={() => handleToggleMessage(message.id)}
                      currentUserId={selectedRequest?.userId || 0}
                    />
                  ))
                )}
              </Box>
            </Grid>

            <Divider orientation="vertical" flexItem />
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <CustomButton color="secondary" onClick={handleCancel}>
          Cancelar
        </CustomButton>
        <CustomButton color="primary" onClick={handleAccept}>
          Guardar ({selectedCount})
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
